import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration, Plugin } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { IconComponent, TypographyComponent } from 'components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import {
	EmotionDistribution,
	GetSocialListeningMyBrandAnalyticsEmotionDistributionService
} from '../../../../../../services/get-social-listening-my-brand-analytics-emotion-distribution.service';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

const EMOTION_LABELS = [
	'Love',
	'Joy',
	'Surprise',
	'Anger',
	'Sadness',
	'Fear',
	'Disgust'
];
const EMOTION_KEYS: (keyof EmotionDistribution)[] = [
	'love',
	'joy',
	'surprise',
	'anger',
	'sadness',
	'fear',
	'disgust'
];
// Categorical emotion palette drawn from the LOLLY report token set
// (Figma 13302:100697 — the emotion swatches). Colors are matched to the
// emotion name where the Figma mockup names it (Joy/Surprise/Anger/Sadness/
// Fear/Disgust); Love has no Figma swatch, so it takes the remaining accent.
const EMOTION_COLORS = [
	'#a98cf0', // Love
	'#f5c451', // Joy
	'#f0a3c6', // Surprise
	'#e94f6a', // Anger
	'#5b8def', // Sadness
	'#9aa3bb', // Fear
	'#c4b8a0' // Disgust
];

const N = EMOTION_LABELS.length;
const SEG_ANGLE = (2 * Math.PI) / N;
const SCALE_START_ANGLE_DEG = -180 / N;

const polarAreaLabelsPlugin: Plugin<'polarArea'> = {
	id: 'polarAreaLabels',

	beforeDatasetsDraw(chart) {
		const ctx = chart.ctx;
		const scale = (chart as any).scales['r'];
		if (!scale) return;
		const cx: number = scale.xCenter;
		const cy: number = scale.yCenter;
		const outerRadius: number = scale.drawingArea;
		ctx.save();
		ctx.strokeStyle = '#e6eaf2';
		ctx.lineWidth = 1.5;
		for (let i = 0; i < N; i++) {
			const angle = -Math.PI / 2 - SEG_ANGLE / 2 + SEG_ANGLE * i;
			ctx.beginPath();
			ctx.moveTo(cx, cy);
			ctx.lineTo(
				cx + Math.cos(angle) * outerRadius,
				cy + Math.sin(angle) * outerRadius
			);
			ctx.stroke();
		}
		ctx.restore();
	},

	afterDraw(chart) {
		const scale = (chart as any).scales['r'];
		if (!scale) return;
		const ctx = chart.ctx;
		const cx: number = scale.xCenter;
		const cy: number = scale.yCenter;
		const labelRadius: number = scale.drawingArea + 22;
		ctx.save();
		ctx.font = 'bold 12px Gilroy, sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillStyle = '#0e1a33';
		EMOTION_LABELS.forEach((label, i) => {
			const midAngle = -Math.PI / 2 + SEG_ANGLE * i;
			const x = cx + Math.cos(midAngle) * labelRadius;
			const y = cy + Math.sin(midAngle) * labelRadius;
			ctx.save();
			ctx.translate(x, y);
			let rot = midAngle + Math.PI / 2;
			if (Math.sin(midAngle) > 0) rot += Math.PI;
			ctx.rotate(rot);
			ctx.fillText(label, 0, 0);
			ctx.restore();
		});
		ctx.restore();
	}
};

export interface EmotionTableRow {
	id: string;
	label: string;
	color: string;
	sharePct: string;
	mentions: string;
}

@Component({
	standalone: true,
	selector: 'app-emotion-analysis-slide',
	templateUrl: './emotion-analysis-slide.component.html',
	styleUrls: ['./emotion-analysis-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class EmotionAnalysisSlideComponent implements OnChanges, OnDestroy {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();
	private destroy$ = new Subject<void>();

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';

	chartData: ChartConfiguration<'polarArea'>['data'] | null = null;
	readonly chartOptions: ChartConfiguration<'polarArea'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		// No entry animation — the PDF export captures the first synchronous
		// frame, so an animating chart would be caught half-drawn (mirrors the
		// mentions-donut slide).
		animation: false,
		layout: { padding: 40 },
		scales: {
			r: {
				startAngle: SCALE_START_ANGLE_DEG,
				ticks: { display: false },
				grid: { color: '#e6eaf2' }
			}
		},
		plugins: {
			legend: { display: false },
			tooltip: {
				callbacks: {
					label: ctx => ` ${ctx.label}: ${ctx.parsed.r}`
				}
			}
		}
	};
	readonly chartType = 'polarArea' as const;
	readonly chartPlugins = [polarAreaLabelsPlugin];

	tableRows: EmotionTableRow[] = [];
	emotionCount = '';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'emotion_analysis',
		null,
		null
	);

	trackByEmotion(_index: number, row: EmotionTableRow): string {
		return row.id;
	}

	private cachedDistribution: EmotionDistribution | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedDistribution ||
			!hasPositiveTotal(
				EMOTION_KEYS.map(key => this.cachedDistribution![key])
			)
		);
	}

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsEmotionDistributionService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedDistribution && this.cacheKey === currentCacheKey) {
			this.applyDistribution(this.cachedDistribution);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedDistribution = data;
				this.cacheKey = currentCacheKey;
				this.applyDistribution(data);
			});
	}

	private applyDistribution(data: EmotionDistribution): void {
		const rawSlide = this.buildSlide(data);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, data);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(data: EmotionDistribution): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		const values = EMOTION_KEYS.map(key => data[key] ?? 0);

		const chartWidget: ChartWidget = {
			id: `${prefix}_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'polarArea',
				dataSourceKey: 'emotion_distribution',
				seriesMeta: EMOTION_LABELS.map((label, index) => ({
					key: label.toLowerCase(),
					label,
					color: EMOTION_COLORS[index]
				})),
				chartJsConfig: {
					type: 'polarArea',
					data: {
						labels: EMOTION_LABELS,
						datasets: [
							{
								data: values,
								backgroundColor: EMOTION_COLORS,
								borderWidth: 0
							}
						]
					}
				}
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'emotion_analysis',
			title: this.item.title || 'Emotion Analysis',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Emotion Analysis',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				chartWidget
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		data: EmotionDistribution
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		const values = EMOTION_KEYS.map(key => data[key] ?? 0);
		const total = values.reduce((sum, value) => sum + value, 0);

		const chartWidget = slide.widgets.find(
			widget => widget.kind === 'chart' && !hidden.has(widget.id)
		) as ChartWidget | undefined;

		const resolvedColors =
			chartWidget?.content.seriesMeta?.map(
				series => series.color ?? EMOTION_COLORS[0]
			) ?? EMOTION_COLORS;

		this.chartData = {
			labels: EMOTION_LABELS,
			datasets: [
				{
					data: values,
					backgroundColor: resolvedColors,
					borderWidth: 0
				}
			]
		};

		this.tableRows = EMOTION_LABELS.map((label, index) => ({
			id: EMOTION_KEYS[index],
			label,
			color: resolvedColors[index] ?? EMOTION_COLORS[index],
			sharePct:
				total > 0
					? `${((values[index] / total) * 100).toFixed(1)}%`
					: '0.0%',
			mentions: values[index].toLocaleString('en-US')
		}));

		// KPI callout — number of emotion categories tracked by the model.
		this.emotionCount = String(this.tableRows.length);

		// An emotion per label, in the colour the breakdown table beside it
		// gives that emotion.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: EMOTION_LABELS,
				series: [{ key: 'mentions', label: 'Mentions', values }],
				labelColors: this.tableRows.map(row => row.color),
				labelColumn: 'Emotion'
			})
		);
	}
}
